import type { EditorState } from 'lexical';

import type { LanguageEnum } from 'js/src/client';
import { DashboardFlowEnum } from 'js/src/client';
import type { DeepReadonly, SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';

/* ACTION TYPES */
export enum SCRIPT_COMPOSER_ACTIONS {
    SET_CURRENT_DOC_TO_SCRIPT_PAGE = 'SET_CURRENT_DOC_TO_SCRIPT_PAGE',
    SET_DOC_TO_SCRIPT_DATA = 'SET_DOC_TO_SCRIPT_DATA',
    SET_SCRIPT_FOR_SCRIPT_OPTION = 'SET_SCRIPT_FOR_SCRIPT_OPTION',
    REMOVE_SCRIPT_FROM_CACHE = 'REMOVE_SCRIPT_FROM_CACHE',
    RESET_SCRIPT_CACHE = 'RESET_SCRIPT_CACHE',
    SET_DOC_TO_SCRIPT_OPTIONS_DATA = 'SET_DOC_TO_SCRIPT_OPTIONS_DATA',
    SET_IS_FETCHING_SCRIPT_OPTION_AI_SCRIPT = 'SET_IS_FETCHING_SCRIPT_OPTION_AI_SCRIPT',
    SET_IS_FETCHING_AI_SCRIPT_OPTIONS = 'SET_IS_FETCHING_AI_SCRIPT_OPTIONS',
    SET_IS_FETCHING_SCRIPT_FROM_IDEA = 'SET_IS_FETCHING_SCRIPT_FROM_IDEA',
    UPDATE_DOC_TO_SCRIPT_OPTION = 'UPDATE_DOC_TO_SCRIPT_OPTION',
    SET_SELECTED_SCRIPT_OPTION = 'SET_SELECTED_SCRIPT_OPTION',
    SET_EDITED_SCRIPT = 'SET_EDITED_SCRIPT',
    SET_EDITED_SCRIPT_INITIAL_WORD_COUNT = 'SET_EDITED_SCRIPT_INITIAL_WORD_COUNT',
    SET_EDITED_SCRIPT_LANGUAGE = 'SET_EDITED_SCRIPT_LANGUAGE',
    SET_LONGEST_SUPPORTED_SCRIPT_DURATION = 'SET_LONGEST_SUPPORTED_SCRIPT_DURATION',
    SET_CURRENT_AI_AVATAR_VOICE_PRESET = 'SET_CURRENT_AI_AVATAR_VOICE_PRESET',
    SET_CURRENT_AI_AVATAR = 'SET_CURRENT_AI_AVATAR',
    SET_CURRENT_AI_VOICE = 'SET_CURRENT_AI_VOICE',
    SET_DOC_TO_SCRIPT_IMPORT_TYPE = 'SET_DOC_TO_SCRIPT_IMPORT_TYPE',
    SET_IDEA_OUTLINE_SUBJECT = 'SET_IDEA_OUTLINE_SUBJECT',
    SET_IDEA_OUTLINE_REFERENCES = 'SET_IDEA_OUTLINE_REFERENCES',
    ADD_IDEA_OUTLINE_REFERENCE = 'ADD_IDEA_OUTLINE_REFERENCE',
    DELETE_IDEA_OUTLINE_REFERENCE = 'DELETE_IDEA_OUTLINE_REFERENCE',
    SET_IS_UPLOADING_SCRIPT_IMAGES = 'SET_IS_UPLOADING_SCRIPT_IMAGES',
    SET_TARGET_DURATION = 'SET_TARGET_DURATION',
    SET_GROUNDHOG_ANALYSIS = 'SET_GROUNDHOG_ANALYSIS',
    CLEAR_GROUNDHOG_ANALYSIS_FOR_TEXT = 'CLEAR_GROUNDHOG_ANALYSIS_FOR_TEXT',
    SET_SELECTED_BLOCK_TEXT = 'SET_SELECTED_BLOCK_TEXT',
    SET_IS_CONVERTING_TO_VOICEOVER = 'SET_IS_CONVERTING_TO_VOICEOVER',
    SET_ENFORCE_BLOCK_SEGMENTATION = 'SET_ENFORCE_BLOCK_SEGMENTATION',
    SET_IS_SEGMENTATION_POPOVER_OPEN = 'SET_IS_SEGMENTATION_POPOVER_OPEN',
}

export enum DOC_TO_SCRIPT_PAGE {
    INITIAL_SCRIPT = 'initial',
    SUMMARIZED_SCRIPT = 'summarized',
    OPTION_SELECTION = 'option-selection',
    BLUEPRINT_SETTINGS = 'blueprint-settings',
    EDIT_SCRIPT = 'edit',
    TALKING_HEAD_UPLOAD_RECORD = 'talking-head-upload-record',
    VOICEOVER_UPLOAD_RECORD = 'voiceover-upload-record',
    SCREEN_RECORDING = 'screen-recording',
}

export type ScriptSection = {
    header: string;
    sentences: Array<string>;
};

export type Script = Array<ScriptSection>;
export type ScriptSegment = { text: string; wordCount: number };

export type DocToScriptData = {
    url?: string;
    originalEditorState?: EditorState;
    editedEditorState?: EditorState;
    shouldSkipAISummarization?: boolean;
};

// this is the ScriptOption we get from the server. We add a uuid to it to make it trackable in the frontend.
export type RawScriptOption = {
    type: string;
    title: string;
    description: string;
    duration_seconds: number;
    tone?: string; // not always used
    perspective?: string; // not always used
    include_hook?: boolean; // not always used
    include_cta?: boolean; // not always used
};

export type ScriptOption = RawScriptOption & {
    uuid: string;
};

export type ScriptOptionsLoadingStatus = {
    scriptId: string;
    isLoading: boolean;
};

export type ScriptControlSetting = {
    title: string;
    description: string;
    duration_seconds: number;
    tone?: string; // not always used
    blueprintId?: number; // not always used
};

type FieldType<T> = {
    [K in keyof T]: T[K];
};

export type ScriptControlField = FieldType<ScriptControlSetting>;

export type ScriptOptionsMap = { [k: string]: ScriptOption[] };
export type ScriptMap = { [k: string]: Script };

export type ScriptComposerData = {
    page: DOC_TO_SCRIPT_PAGE | null;
    edited_script?: Script;
    edited_script_initial_word_count: number;
    edited_script_language: LanguageEnum | null;
    selected_script_option?: ScriptOption;
    script_options: ScriptOptionsMap;
    script_cache: ScriptMap;
    longest_supported_script_duration: number | null;
    skip_ai_summarization_stage: boolean;
    selected_ai_voice: string | null;
    selected_ai_avatar_voice_preset: number | null;
    selected_ai_avatar: number | null;
    import_type: SCRIPT_CREATION_SOURCES | null;
    idea_outline_data: IdeaOutlineValueSet;
    is_converting_to_voiceover: boolean;
    dashboard_flow?: DashboardFlowEnum | null;
};

export type ScriptOptionDurationValue = {
    value: number;
    label: string;
    disabled: boolean;
    disabledLabel: string;
};

export type ScriptOptionsResult = {
    script_options: RawScriptOption[];
};

export type IdeaOutlineValueSet = {
    subject: string;
    use_case: string;
    references: Array<string>;
};

export type ReadonlyIdeaOutlineValueSet = DeepReadonly<IdeaOutlineValueSet>;

export const DASHBOARD_VIDEO_FLOWS_TARGET_PAGE: Record<
    DashboardFlowEnum,
    DOC_TO_SCRIPT_PAGE | null
> = {
    [DashboardFlowEnum.START_FROM_SCRATCH]: null,
    [DashboardFlowEnum.TALKING_HEAD_UPLOAD_RECORD]: DOC_TO_SCRIPT_PAGE.TALKING_HEAD_UPLOAD_RECORD,
    [DashboardFlowEnum.VOICEOVER_UPLOAD_RECORD]: DOC_TO_SCRIPT_PAGE.VOICEOVER_UPLOAD_RECORD,
    [DashboardFlowEnum.SCREEN_RECORDING]: null,
};
